/**
 * Read single entries out of a zip IN THE BROWSER without loading the file.
 *
 * Only small ranges are ever read — the end-of-central-directory record (plus
 * the zip64 records when present), the central directory, and the entry
 * itself — so pulling a manifest out of a multi-gigabyte archive costs a few MB
 * at most. `Blob.arrayBuffer()` on the whole file is not an option: browsers
 * refuse blobs over 2 GiB.
 *
 * Every function returns null for an archive it can't read, so callers decide
 * whether that is fatal.
 */

const EOCD_SIGNATURE = 0x06054b50;
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50;
const ZIP64_EOCD_SIGNATURE = 0x06064b50;
const CENTRAL_HEADER_SIGNATURE = 0x02014b50;
const LOCAL_HEADER_SIGNATURE = 0x04034b50;
const ZIP64_EXTRA_ID = 0x0001;

/** EOCD is 22 bytes plus a comment of at most 64 KB. */
const MAX_EOCD_SCAN = 22 + 0xffff;
const ZIP64_LOCATOR_LENGTH = 20;
const ZIP64_EOCD_LENGTH = 56;
/** 0xFFFFFFFF in a 32-bit field means "the real value is in the zip64 record". */
const ZIP64_SENTINEL = 0xffffffff;

export interface ZipCentralEntry {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  localHeaderOffset: number;
}

async function readView(file: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await file.slice(start, end).arrayBuffer());
}

/** The entries listed in the central directory, or null if it can't be found. */
export async function readZipCentralDirectory(file: Blob): Promise<ZipCentralEntry[] | null> {
  const size = file.size;
  if (size < 22) return null;

  const tailStart = size - Math.min(size, MAX_EOCD_SCAN);
  const tail = await readView(file, tailStart, size);

  // Scan backwards for the record, and VALIDATE each candidate: the signature
  // can legally appear inside the zip comment, so "the last match" is not the
  // answer — a decoy sits AFTER the real record and would win. The real record
  // is the one whose declared comment length runs exactly to end-of-file.
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) !== EOCD_SIGNATURE) continue;
    if (i + 22 + tail.getUint16(i + 20, true) === tail.byteLength) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return null;

  let directorySize = tail.getUint32(eocd + 12, true);
  let directoryOffset = tail.getUint32(eocd + 16, true);

  if (directoryOffset === ZIP64_SENTINEL || directorySize === ZIP64_SENTINEL) {
    const locatorStart = tailStart + eocd - ZIP64_LOCATOR_LENGTH;
    if (locatorStart < 0) return null;
    const locator = await readView(file, locatorStart, locatorStart + ZIP64_LOCATOR_LENGTH);
    if (locator.getUint32(0, true) !== ZIP64_LOCATOR_SIGNATURE) return null;

    const recordStart = Number(locator.getBigUint64(8, true));
    const record = await readView(file, recordStart, recordStart + ZIP64_EOCD_LENGTH);
    if (record.byteLength < ZIP64_EOCD_LENGTH) return null;
    if (record.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE) return null;
    directorySize = Number(record.getBigUint64(40, true));
    directoryOffset = Number(record.getBigUint64(48, true));
  }
  if (directoryOffset + directorySize > size) return null;

  const view = await readView(file, directoryOffset, directoryOffset + directorySize);
  const decoder = new TextDecoder();
  const entries: ZipCentralEntry[] = [];

  let p = 0;
  while (p + 46 <= view.byteLength) {
    if (view.getUint32(p, true) !== CENTRAL_HEADER_SIGNATURE) break;
    const nameLength = view.getUint16(p + 28, true);
    const extraLength = view.getUint16(p + 30, true);
    const commentLength = view.getUint16(p + 32, true);

    let compressedSize = view.getUint32(p + 20, true);
    const uncompressedSize = view.getUint32(p + 24, true);
    let localHeaderOffset = view.getUint32(p + 42, true);

    if (
      compressedSize === ZIP64_SENTINEL ||
      uncompressedSize === ZIP64_SENTINEL ||
      localHeaderOffset === ZIP64_SENTINEL
    ) {
      // The zip64 extra field holds, in this order, only the values whose
      // 32-bit field is the sentinel: uncompressed, compressed, offset.
      const extraStart = p + 46 + nameLength;
      let q = extraStart;
      while (q + 4 <= extraStart + extraLength) {
        const id = view.getUint16(q, true);
        const length = view.getUint16(q + 2, true);
        if (id === ZIP64_EXTRA_ID) {
          let at = q + 4;
          if (uncompressedSize === ZIP64_SENTINEL) at += 8;
          if (compressedSize === ZIP64_SENTINEL) {
            compressedSize = Number(view.getBigUint64(at, true));
            at += 8;
          }
          if (localHeaderOffset === ZIP64_SENTINEL) {
            localHeaderOffset = Number(view.getBigUint64(at, true));
          }
          break;
        }
        q += 4 + length;
      }
    }

    entries.push({
      name: decoder
        .decode(new Uint8Array(view.buffer, view.byteOffset + p + 46, nameLength))
        .replace(/\\/g, '/'),
      compressionMethod: view.getUint16(p + 10, true),
      compressedSize,
      localHeaderOffset,
    });
    p += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

/** The entry's uncompressed bytes, or null for an unsupported method or a bad header. */
export async function readZipEntryBytes(
  file: Blob,
  entry: ZipCentralEntry,
): Promise<Uint8Array | null> {
  // The local header repeats the name/extra lengths, and its extra field may
  // differ in length from the central one — so the data offset must be computed
  // from the LOCAL header, not the central one.
  const header = await readView(file, entry.localHeaderOffset, entry.localHeaderOffset + 30);
  if (header.byteLength < 30) return null;
  if (header.getUint32(0, true) !== LOCAL_HEADER_SIGNATURE) return null;

  const dataStart =
    entry.localHeaderOffset + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  const raw = await file.slice(dataStart, dataStart + entry.compressedSize).arrayBuffer();

  if (entry.compressionMethod === 0) return new Uint8Array(raw); // stored
  if (entry.compressionMethod !== 8) return null; // not deflate
  return inflateRaw(raw);
}

async function inflateRaw(data: ArrayBuffer): Promise<Uint8Array | null> {
  // Not universal (Safari < 16.4, Firefox < 113).
  if (typeof DecompressionStream === 'undefined') return null;

  const ds = new DecompressionStream('deflate-raw');
  // Write and read concurrently: awaiting the write first would deadlock once a
  // payload exceeds the stream's buffer.
  const writer = ds.writable.getWriter();
  void writer.write(new Uint8Array(data)).then(() => writer.close());

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.length;
    }
  }

  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}
